import React from 'react';
import { AlertTriangle, Clock, Star } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';

export type DashboardSection = 'overdue' | 'today' | 'thisWeek' | 'highlighted';

interface MobileDashboardColumnSwitcherProps {
  activeSection: DashboardSection;
  onSectionSelect: (section: DashboardSection) => void;
  sectionCounts: Record<DashboardSection, number>;
}

const SECTION_CONFIG = {
  overdue: {
    icon: AlertTriangle,
    label: 'Overdue',
    bgColor: 'bg-red-500',
    inactiveBgColor: 'bg-red-100',
    textColor: 'text-red-500',
  },
  today: {
    icon: Clock,
    label: 'Today',
    bgColor: 'bg-green-500',
    inactiveBgColor: 'bg-green-100',
    textColor: 'text-green-500',
  },
  thisWeek: {
    icon: Clock,
    label: 'This Week',
    bgColor: 'bg-blue-500',
    inactiveBgColor: 'bg-blue-100',
    textColor: 'text-blue-500',
  },
  highlighted: {
    icon: Star,
    label: 'Highlighted',
    bgColor: 'bg-yellow-500',
    inactiveBgColor: 'bg-yellow-100',
    textColor: 'text-yellow-500',
  },
};

export function MobileDashboardColumnSwitcher({
  onSectionSelect,
  sectionCounts,
}: MobileDashboardColumnSwitcherProps) {
  const sections: DashboardSection[] = ['highlighted', 'overdue', 'today', 'thisWeek'];
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  return (
    <div
      className="fixed left-0 right-0 flex items-center justify-center h-14 z-[40] pointer-events-none"
      style={{
        top: isNativeApp ? `${statusBarHeight}px` : 0,
        height: '3.5rem', // 14 in tailwind = 3.5rem
      }}
    >
      <div className="flex items-center space-x-2 pointer-events-auto">
        {sections.map(section => {
          const config = SECTION_CONFIG[section];
          const Icon = config.icon;

          return (
            <button
              key={section}
              onClick={() => onSectionSelect(section)}
              className={cn(
                'h-8 flex items-center px-3 rounded-md transition-colors duration-150 shadow-sm',
                `${config.bgColor} text-white` // Always use colored background and white text
              )}
              data-section-id={section}
              aria-label={config.label}
            >
              <Icon size={14} /> {/* Always white icon */}
              <span className="text-xs font-medium ml-1.5 text-white">
                {sectionCounts[section]} {/* Always show count, even when zero */}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
